/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { convertVisitToPhoneCall } from '../../../../actions/areas/planner';
import useTimeline from '../../timeline/useTimeline';
import globals from '../../../../globals/communityGlobals';
import { maxDuration, careTypes } from '../../../../settings/constants';
import { parseDateUtc, trimmedDateAt } from '../../../../utils/dates';
import { useFetchActivitiesOptions } from '../../../../actions/areas/planner/fetchHooks';

const locale = globals.getArea('treatments');
const now = parseDateUtc(globals.now);

export default function ConvertTreatmentInstanceType({ treatmentDetails, toggleRefresh }) {
  const errorsLocale = globals.getArea('error');
  const {
    assignment,
    refreshClinicianTreatmentsAndClosePanels,
    getAccessToken,
    getFlag,
    setFlag,
    setAlertMessage,
  } = useTimeline();

  const [filteredActivitiesOptions, setFilteredActivitiesOptions] = useState([]);
  const [selectedActivitiesIds, setActivities] = useState([]);
  const [duration, setDuration] = useState('');
  const [isDataLoading, setIsDataLoading] = useState(false);

  const orgUnitId = useSelector(state => state.areas.dailyPlanning.filters.selectedTeam);

  const getMaxBand = () => {
    return assignment ? treatmentDetails.currentAssignedClinician.clinicianBand : null;
  };

  const getSkillsIds = () => {
    return assignment
      ? treatmentDetails.currentAssignedClinician.clinicianSkills
          .filter(skill => {
            return parseDateUtc(skill.validTo).isSameOrAfter(now) && parseDateUtc(skill.validFrom).isSameOrBefore(now);
          })
          .map(item => item.skill.id)
      : null;
  };

  const filterActivitiesByOrgUnit = activitiesOptions => {
    if (!orgUnitId) {
      setFilteredActivitiesOptions(activitiesOptions);
    }

    const filteredActivities = activitiesOptions.filter(
      o => o.orgUnitsIds.length === 0 || o.orgUnitsIds.includes(orgUnitId),
    );
    setFilteredActivitiesOptions(filteredActivities);
  };

  const getActivitiesOptionsModel = {
    visitType: careTypes.phoneCall,
    maxBand: getMaxBand(),
    clinicianSkillsIds: getSkillsIds(),
  };
  const { data: activitiesOptions } = useFetchActivitiesOptions(getActivitiesOptionsModel, isDataLoading);

  useEffect(() => {
    if (activitiesOptions) {
      filterActivitiesByOrgUnit(activitiesOptions);
    }
  }, [activitiesOptions]);

  const resetAndReloadError = message => {
    setIsDataLoading(false);
    setAlertMessage(message);
  };

  const convertVisit = () => {
    setIsDataLoading(true);

    const treatmentInstance = treatmentDetails.treatmentInstance;
    const treatment = treatmentDetails.treatment;

    const model = {
      dateOn: trimmedDateAt(treatmentInstance.occurrence),
      treatmentInstanceId: treatmentInstance.id,
      treatmentId: treatmentDetails.treatment.id,
      activitiesIds: selectedActivitiesIds,
      duration,
      specificTimeFrom: treatment.isSpecific ? treatment.specificTimeFrom : null,
      timeSlotMatrix: !treatment.isSpecific
        ? treatment.isMultiple
          ? treatmentInstance.selectedTimeSlots
          : treatment.timeSlotsMatrix
        : null,
    };

    convertVisitToPhoneCall(
      getAccessToken,
      model,
      () => {
        setIsDataLoading(false);
        refreshClinicianTreatmentsAndClosePanels();
        toggleRefresh();
      },
      resetAndReloadError,
    );
  };

  const calculateDuration = value => {
    const durationn = value === 0 ? '' : value;
    setDuration(durationn);
  };

  const setActivitiesDuration = values => {
    setActivities(values);

    const activitiesDuration = activitiesOptions
      .filter(activity => values.includes(activity.id))
      .reduce((sum, activity) => sum + activity.duration, 0);

    calculateDuration(activitiesDuration);
  };

  const isDurationValueValid = () => {
    return duration <= maxDuration && duration > 0;
  };

  const canSave = () => selectedActivitiesIds.length && isDurationValueValid() && !isDataLoading;

  const headerInfo = () => {
    return (
      <div className="grey-sidePanel-header">
        <div className="sidePanel-header-subtitle">
          <Box flexGrow={1} py={1}>
            <Typography variant="subtitle1">{locale.getMsg('convertVisitSidePanelTitle')}</Typography>
          </Box>
        </div>
        <div className="sidePanel-header-title-wrap">
          <div className="sidePanel-header-title">
            <Typography variant="h6" gutterBottom>
              {locale.getMsg('convertVisitMessage')}
            </Typography>
          </div>
        </div>
      </div>
    );
  };

  const mainContent = () => {
    return (
      <div className="reschedule-sidePanel-form">
        <Box mt={2}>
          <Typography variant="h6" gutterBottom>
            Activities*
          </Typography>
          <Box py={1}>
            <Select
              multiple
              onChange={e => setActivitiesDuration(e.target.value)}
              labelId="map-to-teams"
              value={selectedActivitiesIds}
              fullWidth
            >
              {filteredActivitiesOptions &&
                filteredActivitiesOptions.map(activity => (
                  <MenuItem key={activity.id} value={activity.id}>
                    {activity.name}
                  </MenuItem>
                ))}
            </Select>
          </Box>
          <Box className="rescheduleVisit-textField" pt={2}>
            <Typography variant="h6" gutterBottom>
              Edit duration*
            </Typography>
            <TextField
              required
              fullWidth
              type="number"
              InputProps={{ inputProps: { min: 1, max: 1440 } }}
              onChange={e => setDuration(e.target.value)}
              value={duration != null ? duration : null}
              helperText={
                duration !== '' && !isDurationValueValid()
                  ? errorsLocale.getMsg('activityMaxDuration', [`${maxDuration}`])
                  : ''
              }
              error={duration !== '' && !isDurationValueValid()}
            />
          </Box>
        </Box>
      </div>
    );
  };

  const actionButtons = () => {
    return (
      <div className="sidePanel-footer">
        <div className="sidePanel-buttons">
          <div className="sidePanel-BackButton">
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setFlag('showConvertVisitSidePanel', false);
              }}
            >
              Back
            </Button>
          </div>
          <div className="sidePanel-saveButton">
            {!isDataLoading && (
              <Button variant="contained" color="primary" disabled={!canSave()} onClick={convertVisit}>
                Save
              </Button>
            )}
            {isDataLoading && (
              <Button variant="contained" color="primary">
                <CircularProgress style={{ marginLeft: '5px' }} size={18} color="inherit" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="side-panel-content-wrapper">
      <SwipeableDrawer
        anchor="right"
        open={getFlag('showConvertVisitSidePanel') === true}
        onOpen={() => {}}
        onClose={() => setFlag('showConvertVisitSidePanel', false)}
        id="note"
      >
        <div className="sidePanel-wrapper" style={{ width: '350px' }} id="add-noted-side-panel">
          {headerInfo()}
          {mainContent()}
          {actionButtons()}
        </div>
      </SwipeableDrawer>
    </section>
  );
}
